import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, X, Plus, Save } from 'lucide-react';
import { PlanService, Panel, ErrorBanner } from './PlanShared';

const Field = ({ label, error, children }) => (
    <div>
        <label className="block text-sm font-medium mb-1.5 text-gray-600">{label}</label>
        {children}
        {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
);

const emptyForm = { name: '', description: '', price: 0, planType: 'monthly', discountType: 'percentage', discountAmount: 0, features: [''], status: 'active' };

const AddEditPlan = () => {
    const navigate = useNavigate();
    const { id } = useParams(); 
    const isEditMode = Boolean(id);

    const [formData, setFormData] = useState(emptyForm);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(isEditMode);
    const [loadError, setLoadError] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    const loadPlan = useCallback(async () => {
        if (!isEditMode) {
            setFormData(emptyForm);
            setLoading(false);
            return;
        }
        setLoading(true);
        setLoadError(null);
        try {
            const plan = await PlanService.getById(id);
            if (!plan) {
                setNotFound(true);
            } else {
                setFormData({
                    name: plan.name || '',
                    description: plan.description || '',
                    price: plan.price || 0,
                    planType: plan.planType || 'monthly',
                    discountType: plan.discountType || 'percentage',
                    discountAmount: plan.discountAmount || 0,
                    features: plan.features?.length ? plan.features : [''],
                    status: plan.status || 'active'
                });
            }
        } catch (err) {
            setLoadError(err.message);
        } finally {
            setLoading(false);
        }
    }, [id, isEditMode]);

    useEffect(() => { loadPlan(); }, [loadPlan]);

    const validate = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Plan name is required';
        if (!formData.description.trim()) newErrors.description = 'Description is required';
        if (formData.price <= 0) newErrors.price = 'Price must be greater than 0';
        if (!['monthly', 'yearly'].includes(formData.planType)) newErrors.planType = 'Plan type is required';
        if (formData.discountAmount < 0) newErrors.discountAmount = 'Discount cannot be negative';
        if (formData.discountType === 'percentage' && formData.discountAmount > 100) {
            newErrors.discountAmount = 'Percentage discount cannot exceed 100%';
        }
        if (!formData.features.some(f => f.trim())) newErrors.features = 'At least one feature is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        const payload = { ...formData, features: formData.features.filter(f => f.trim()) };

        setSubmitting(true);
        setSubmitError(null);
        try {
            if (isEditMode) {
                await PlanService.update(id, payload);
                navigate(`/admin/plans/${id}`);
            } else {
                const newPlan = await PlanService.create(payload);
                navigate(`/admin/plans/${newPlan.id}`);
            }
        } catch (err) {
            setSubmitError(err.message);
            setSubmitting(false);
        }
    };

    const goBack = () => navigate(isEditMode ? `/admin/plans/${id}` : '/admin/plans');

    const addFeature = () => setFormData(prev => ({ ...prev, features: [...prev.features, ''] }));
    const removeFeature = (index) => setFormData(prev => ({ ...prev, features: prev.features.filter((_, i) => i !== index) }));
    const updateFeature = (index, value) => setFormData(prev => ({ ...prev, features: prev.features.map((f, i) => i === index ? value : f) }));

    if (loading) {
        return (
            <div className="bg-gray-50 min-h-screen p-4 rounded-2xl">
                <div className="text-center py-24">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-orange-400 border-t-transparent mx-auto" />
                    <p className="text-sm mt-4 text-gray-500">Loading plan...</p>
                </div>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="bg-gray-50 min-h-screen p-4 rounded-2xl space-y-4">
                <ErrorBanner message={loadError} onRetry={loadPlan} />
                <button onClick={() => navigate('/admin/plans')} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl text-orange-500 hover:bg-orange-50">
                    <ArrowLeft size={16} /> Back to Plans
                </button>
            </div>
        );
    }

    if (notFound) {
        return (
            <div className="bg-gray-50 min-h-screen p-4 rounded-2xl">
                <div className="text-center py-24 rounded-2xl bg-white border border-orange-200">
                    <p className="text-sm text-gray-500">Plan not found</p>
                    <button onClick={() => navigate('/admin/plans')} className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl text-orange-500 hover:bg-orange-50">
                        <ArrowLeft size={16} /> Back to Plans
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4 sm:space-y-6 px-2 sm:px-0 bg-gray-50 min-h-screen p-4 rounded-2xl">
            {/* Header */}
            <div className="flex items-center gap-3 bg-white p-4 rounded-2xl border border-orange-200 shadow-sm">
                <button onClick={goBack} className="p-2 rounded-xl transition-colors text-gray-500 hover:bg-orange-50 hover:text-orange-500 shrink-0">
                    <ArrowLeft size={18} />
                </button>
                <div>
                    <h1 className="text-lg sm:text-xl font-bold text-gray-800">{isEditMode ? 'Edit Plan' : 'Create New Plan'}</h1>
                    <p className="text-xs sm:text-sm mt-0.5 text-gray-500">
                        {isEditMode ? 'Update the details of this subscription plan' : 'Set up a new subscription plan'}
                    </p>
                </div>
            </div>

            {submitError && <ErrorBanner message={submitError} />}

            <Panel>
                <form onSubmit={handleSubmit} className="space-y-5">
                    <Field label="Plan Name *" error={errors.name}>
                        <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
                            className="w-full px-4 py-2.5 rounded-xl transition-all bg-orange-50 border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200" placeholder="e.g., Professional Plan" />
                    </Field>

                    <Field label="Description *" error={errors.description}>
                        <textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })}
                            rows={3} className="w-full px-4 py-2.5 rounded-xl transition-all resize-none bg-orange-50 border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200" placeholder="Describe what this plan offers..." />
                    </Field>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Price (₹) *" error={errors.price}>
                            <input type="number" min="0" step="0.01" value={formData.price}
                                onChange={e => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                                className="w-full px-4 py-2.5 rounded-xl transition-all bg-orange-50 border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200" />
                        </Field>
                        <Field label="Plan Type *" error={errors.planType}>
                            <select value={formData.planType} onChange={e => setFormData({ ...formData, planType: e.target.value })}
                                className="w-full px-4 py-2.5 rounded-xl transition-all bg-orange-50 border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200">
                                <option value="monthly" className="bg-white">Monthly</option>
                                <option value="yearly" className="bg-white">Yearly</option>
                            </select>
                        </Field>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Discount Type">
                            <select value={formData.discountType} onChange={e => setFormData({ ...formData, discountType: e.target.value })}
                                className="w-full px-4 py-2.5 rounded-xl transition-all bg-orange-50 border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200">
                                <option value="percentage" className="bg-white">Percentage (%)</option>
                                <option value="amount" className="bg-white">Amount (₹)</option>
                            </select>
                        </Field>
                        <Field label="Discount Amount" error={errors.discountAmount}>
                            <input type="number" min="0" step={formData.discountType === 'percentage' ? 1 : 0.01}
                                value={formData.discountAmount} onChange={e => setFormData({ ...formData, discountAmount: parseFloat(e.target.value) || 0 })}
                                className="w-full px-4 py-2.5 rounded-xl transition-all bg-orange-50 border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200" />
                        </Field>
                    </div>

                    <Field label="Features *" error={errors.features}>
                        <div className="space-y-2">
                            {formData.features.map((feature, index) => (
                                <div key={index} className="flex items-center gap-2">
                                    <input type="text" value={feature} onChange={e => updateFeature(index, e.target.value)}
                                        className="flex-1 px-4 py-2.5 rounded-xl transition-all bg-orange-50 border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200" placeholder={`Feature ${index + 1}`} />
                                    <button type="button" onClick={() => removeFeature(index)} disabled={formData.features.length === 1}
                                        className="p-2 rounded-lg transition-colors hover:bg-red-50 disabled:opacity-30 text-red-500">
                                        <X size={18} />
                                    </button>
                                </div>
                            ))}
                            <button type="button" onClick={addFeature} className="flex items-center gap-1.5 text-sm font-medium transition-colors text-orange-500 hover:text-orange-600">
                                <Plus size={16} /> Add Feature
                            </button>
                        </div>
                    </Field>

                    {isEditMode && (
                        <Field label="Status">
                            <select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}
                                className="w-full px-4 py-2.5 rounded-xl transition-all bg-orange-50 border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200">
                                <option value="active" className="bg-white">Active</option>
                                <option value="inactive" className="bg-white">Inactive</option>
                            </select>
                        </Field>
                    )}

                    <div className="flex items-center justify-end gap-3 pt-2 border-t border-orange-100">
                        <button type="button" onClick={goBack} className="px-4 py-2 text-sm font-medium rounded-xl transition-colors text-gray-600 hover:bg-gray-50">
                            Cancel
                        </button>
                        <button type="submit" disabled={submitting} className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white rounded-xl transition-all shadow-sm bg-gradient-to-r from-orange-500 to-orange-600 hover:shadow-orange-500/30 disabled:opacity-60">
                            <Save size={16} /> {submitting ? 'Saving...' : (isEditMode ? 'Update Plan' : 'Create Plan')}
                        </button>
                    </div>
                </form>
            </Panel>
        </div>
    );
};

export default AddEditPlan;